
import React, { useState } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Loader2, Shield, ShieldCheck, UserX } from 'lucide-react';
import { ExtendedCommunityMember } from "@/hooks/useUserProfiles";
import { supabase } from "@/integrations/supabase/client";
import { UserProfile } from "@/types/user";
import { UserRole } from "@/contexts/CommunityMemberContext";
import { CommunityMemberStatus } from "@/types/community";

interface UserProfileSheetProps {
  isOpen: boolean;
  onClose: () => void;
  user: ExtendedCommunityMember;
  onBlock: (memberId: string) => void;
  onUnblock: (memberId: string) => void;
  onRoleUpdate?: (memberId: string, role: UserRole) => Promise<void>;
}

const UserProfileSheet = ({ 
  isOpen, 
  onClose, 
  user, 
  onBlock,
  onUnblock,
  onRoleUpdate 
}: UserProfileSheetProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<UserProfile>>(user.user_profile || {});
  const [selectedRole, setSelectedRole] = useState<UserRole>(user.role_name);
  const [isSaving, setIsSaving] = useState(false);
  const [isRoleUpdating, setIsRoleUpdating] = useState(false);
  const { toast } = useToast();

  const memberStatus: CommunityMemberStatus = user.status || 'ACTIVE';

  const handleRoleChange = async (newRole: UserRole) => {
    if (!onRoleUpdate || newRole === user.role_name) return;

    try {
      setIsRoleUpdating(true);
      setSelectedRole(newRole); // Optimistically update the UI
      await onRoleUpdate(user.id, newRole);
    } catch (error: any) {
      // Revert on error
      setSelectedRole(user.role_name);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to update role"
      });
    } finally {
      setIsRoleUpdating(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const { error: profileError } = await supabase
        .from('user_profiles')
        .update({
          firstname: formData.firstname,
          lastname: formData.lastname,
          country: formData.country,
          town: formData.town,
          postal_code: formData.postal_code?.toUpperCase() || null,
          gender: formData.gender as "Male" | "Female",
          age_range: formData.age_range as "Under 18" | "18-24" | "25-34" | "35-44" | "45-54" | "55-64" | "65+",
          marketing_opt_in: formData.marketing_opt_in
        })
        .eq('id', user.user_id);

      if (profileError) throw profileError;

      // Update auth user metadata
      const { error: authError } = await supabase.auth.updateUser({
        data: {
          first_name: formData.firstname,
          last_name: formData.lastname,
          full_name: `${formData.firstname} ${formData.lastname}`.trim()
        }
      });

      if (authError) throw authError;

      toast({
        title: "Profile updated",
        description: "The user profile has been updated successfully."
      });
      setIsEditing(false);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader className="mb-6">
          <SheetTitle>User Profile</SheetTitle>
          <SheetDescription>
            View and manage user profile information and permissions
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstname">First Name</Label>
              <Input
                id="firstname"
                value={formData.firstname || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, firstname: e.target.value }))}
                disabled={!isEditing}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastname">Last Name</Label>
              <Input
                id="lastname"
                value={formData.lastname || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, lastname: e.target.value }))}
                disabled={!isEditing}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" value={user.user_profile?.email || ''} disabled />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              {onRoleUpdate ? (
                <Select 
                  value={selectedRole} 
                  onValueChange={(value) => handleRoleChange(value as UserRole)}
                  disabled={isRoleUpdating || !onRoleUpdate}
                >
                  <SelectTrigger>
                    <SelectValue>
                      {isRoleUpdating ? (
                        <div className="flex items-center">
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          <span>Updating...</span>
                        </div>
                      ) : (
                        selectedRole
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="reviewer">Reviewer</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Badge variant={
                  user.role_name === 'admin' ? 'default' : 
                  user.role_name === 'reviewer' ? 'secondary' : 'outline'
                } className="ml-2">
                  {user.role_name}
                </Badge>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Badge 
                variant={memberStatus === 'ACTIVE' ? 'outline' : 'destructive'}
                className={`flex items-center gap-1 w-fit ${
                  memberStatus === 'ACTIVE' 
                    ? 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100' 
                    : ''
                }`}
              >
                {memberStatus === 'ACTIVE' ? (
                  <ShieldCheck className="h-3 w-3" />
                ) : (
                  <Shield className="h-3 w-3" />
                )}
                {memberStatus}
              </Badge>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="gender">Gender</Label>
              <Select 
                value={formData.gender || ''} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, gender: value }))}
                disabled={!isEditing}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Male">Male</SelectItem>
                  <SelectItem value="Female">Female</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="age_range">Age Range</Label>
              <Select 
                value={formData.age_range || ''} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, age_range: value }))}
                disabled={!isEditing}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select age range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="18-24">18-24</SelectItem>
                  <SelectItem value="25-34">25-34</SelectItem>
                  <SelectItem value="35-44">35-44</SelectItem>
                  <SelectItem value="45-54">45-54</SelectItem>
                  <SelectItem value="55-64">55-64</SelectItem>
                  <SelectItem value="65+">65+</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="country">Country</Label>
              <Input
                id="country"
                value={formData.country || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                disabled={!isEditing}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="town">Town</Label>
              <Input
                id="town"
                value={formData.town || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, town: e.target.value }))}
                disabled={!isEditing}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="postal_code">Postal Code</Label>
            <Input
              id="postal_code"
              value={formData.postal_code || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, postal_code: e.target.value }))}
              disabled={!isEditing}
            />
          </div>
        </div>

        <div className="mt-6 flex justify-between gap-2">
          {!isEditing ? (
            <>
              <Button onClick={() => setIsEditing(true)}>
                Edit Profile
              </Button>
              <div className="flex gap-2">
                {memberStatus === 'BLOCKED' ? (
                  <Button
                    variant="outline"
                    onClick={() => onUnblock(user.id)}
                    className="text-green-600 hover:text-green-700"
                  >
                    <ShieldCheck className="h-4 w-4 mr-2" />
                    Unblock User
                  </Button>
                ) : (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive">
                        <UserX className="h-4 w-4 mr-2" />
                        Block User
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will block the user from accessing this community. They will not be able to rejoin until an administrator unblocks them.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => onBlock(user.id)}>
                          Continue
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </>
          ) : (
            <>
              <Button onClick={() => setIsEditing(false)} variant="outline">
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? "Saving..." : "Save Changes"}
              </Button>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default UserProfileSheet;
